import { useEffect } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowUpRight, ArrowLeft } from "lucide-react";

const EASE = [0.22, 1, 0.36, 1];

export const PersonaModal = ({ persona, onClose, onBookDemo }) => {
  // Directional effect keyed ONLY on `persona` so Lenis reliably stops on open
  // and resumes on close (avoids stale/asymmetric state from onClose identity churn).
  useEffect(() => {
    if (persona) {
      document.body.style.overflow = "hidden";
      window.__lenis?.stop();
    } else {
      document.body.style.overflow = "";
      window.__lenis?.start();
    }
  }, [persona]);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const warm = persona ? parseInt(persona.index, 10) % 2 === 0 : false;
  const acc = warm ? "text-ember" : "text-teal";
  const accBorder = warm ? "border-ember/25" : "border-teal/25";

  return createPortal(
    <AnimatePresence>
      {persona && (
        <motion.div
          className="fixed inset-0 z-[200] overflow-y-auto bg-obsidian/95 backdrop-blur-xl"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.4 }}
          data-testid={`persona-modal-${persona.index}`}
          data-lenis-prevent
        >
          <div className="pointer-events-none absolute inset-0 spotlight" />

          {/* top bar */}
          <div className="sticky top-0 z-20 flex items-center justify-between border-b border-white/10 bg-obsidian/80 px-6 py-4 backdrop-blur-md md:px-12">
            <button
              onClick={onClose}
              data-testid="persona-modal-back"
              className="group flex items-center gap-3 font-body text-[12px] uppercase tracking-[0.2em] text-white/70 transition-colors duration-300 hover:text-white"
            >
              <ArrowLeft size={16} className="transition-transform duration-300 group-hover:-translate-x-1" />
              Back to personas
            </button>
            <span className={`hidden font-body text-[11px] uppercase tracking-[0.3em] ${acc} sm:block`}>
              Persona {persona.index} — Deep Dive
            </span>
            <button
              onClick={onClose}
              data-testid="persona-modal-close"
              className="flex h-11 w-11 items-center justify-center rounded-full border border-white/20 text-white transition-colors duration-300 hover:border-teal hover:bg-teal hover:text-black"
              aria-label="Close"
            >
              <X size={18} />
            </button>
          </div>

          <motion.div
            initial={{ y: 40, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.6, ease: EASE, delay: 0.05 }}
            className="relative mx-auto max-w-[1400px] px-6 pb-28 pt-10 md:px-12"
          >
            {/* Header: image + title */}
            <div className="grid gap-10 md:grid-cols-2 md:gap-16">
              <div className="relative aspect-[4/5] overflow-hidden rounded-sm border border-white/10">
                <img src={persona.image} alt={persona.name} className="h-full w-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-obsidian/80 via-transparent to-transparent" />
                <span className={`absolute bottom-5 left-5 font-body text-[11px] uppercase tracking-[0.25em] ${acc}`}>
                  {persona.tagline}
                </span>
              </div>

              <div className="flex flex-col justify-center">
                <span className="font-display text-7xl font-bold text-white/10 md:text-8xl">
                  {persona.index}
                </span>
                <h2 className="mt-2 font-display text-4xl font-medium leading-none tracking-tight text-white md:text-6xl">
                  {persona.name}
                </h2>
                <p className="mt-6 max-w-md font-body text-base leading-relaxed text-white/60">
                  {persona.profile}
                </p>
                <div className="mt-6 flex flex-wrap gap-2">
                  {persona.subSegments.map((s) => (
                    <span
                      key={s}
                      className="rounded-full border border-white/15 px-3 py-1.5 font-body text-[11px] uppercase tracking-[0.12em] text-white/60"
                    >
                      {s}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {/* Strategy */}
            <Section title="Targeting Strategy">
              <p className="max-w-3xl font-body text-lg leading-relaxed text-white/70">{persona.strategy}</p>
            </Section>

            {/* Offerings */}
            <Section title="Our Offering">
              <div className="grid gap-px overflow-hidden rounded-xl border border-white/10 bg-white/5 sm:grid-cols-2">
                {persona.offerings.map((o, i) => (
                  <div key={o.title} className="bg-obsidian p-6 md:p-8">
                    <span className={`font-body text-[11px] uppercase tracking-[0.15em] ${acc}`}>
                      0{i + 1}
                    </span>
                    <h4 className="mt-3 font-display text-xl font-medium text-white">{o.title}</h4>
                    <p className="mt-2 font-body text-sm leading-relaxed text-white/50">{o.desc}</p>
                  </div>
                ))}
              </div>
            </Section>

            {/* Case study */}
            <Section title="Case Study">
              <div
                className={`rounded-xl border ${accBorder} bg-white/[0.02] p-8 backdrop-blur-xl md:p-10`}
                style={{ boxShadow: "inset 0 1px 0 0 rgba(255,255,255,0.05)" }}
              >
                <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
                  <span className="font-display text-3xl font-semibold text-white md:text-4xl">
                    {persona.case.brand}
                  </span>
                  <span className={`font-body text-[12px] uppercase tracking-[0.2em] ${acc}`}>
                    {persona.case.project}
                  </span>
                </div>
                <p className="mt-5 max-w-2xl font-body text-base leading-relaxed text-white/60">
                  {persona.case.summary}
                </p>
                <div className="mt-8 flex flex-wrap gap-x-12 gap-y-6">
                  {persona.case.metrics.map((m) => (
                    <div key={m.label}>
                      <div className={`font-display text-4xl font-semibold ${acc} md:text-5xl`}>
                        {m.value}
                      </div>
                      <div className="mt-1 font-body text-[11px] uppercase tracking-[0.12em] text-white/45">
                        {m.label}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </Section>

            {/* Data application / Activation */}
            <Section title="Activation — How We Deploy The Data">
              <div className="grid gap-8 md:grid-cols-[1.4fr_1fr] md:items-center">
                <div>
                  <h4 className="font-display text-2xl font-medium text-white md:text-3xl">
                    {persona.dataApplication.headline}
                  </h4>
                  <p className="mt-4 max-w-xl font-body text-base leading-relaxed text-white/60">
                    {persona.dataApplication.desc}
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-px overflow-hidden rounded-xl border border-white/10 bg-white/5">
                  {persona.dataApplication.stats.map((s) => (
                    <div key={s.label} className="bg-obsidian p-6">
                      <div className={`font-display text-2xl font-semibold ${acc} md:text-3xl`}>
                        {s.value}
                      </div>
                      <div className="mt-1 font-body text-[10px] uppercase tracking-[0.12em] text-white/45">
                        {s.label}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </Section>

            {/* CTA */}
            <button
              onClick={onBookDemo}
              data-testid="persona-modal-book"
              className="group mt-16 flex items-center gap-4 rounded-full border border-teal bg-teal px-8 py-4 font-body text-sm uppercase tracking-[0.2em] text-black transition-colors duration-300 hover:bg-transparent hover:text-teal"
            >
              Activate this persona
              <ArrowUpRight className="transition-transform duration-300 group-hover:rotate-45" size={18} />
            </button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
};

const Section = ({ title, children }) => (
  <div className="mt-16 border-t border-white/10 pt-10">
    <div className="mb-6 flex items-center gap-3">
      <span className="h-1.5 w-1.5 rounded-full bg-teal" />
      <span className="font-body text-[11px] uppercase tracking-[0.25em] text-white/45">{title}</span>
    </div>
    {children}
  </div>
);

export default PersonaModal;
